import { usePortalMessage } from "../../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Accept an invitation — `/invitation#<token>` (plan-014 B2; design §27.15.1; FR12-ONB-04,
 * FR12-TEAM-03). Drawn to the prototype's `invite` page (`design/WeTalk.dc.html`, the Organization block renderer):
 * "Invitation from {inviter}", "Join {organization}", "You were invited as {email}. Accepting gives you exactly the
 * roles below and nothing else.", a list of exactly the offered memberships, and a notice carrying Accept.
 *
 * THE PAGE VERIFIES WHO IS LOOKING (FR12-ONB-04). The API answers whether the signed-in person is the invited one:
 *   nobody signed in        log in here with the invited address, or create a sign-in first (no organization is made);
 *   someone else            "This invitation is for …" with Sign out;
 *   an unverified address   Accept, and accepting verifies the invited address (owner answer 45: the emailed link is
 *                           the proof — no second verification email);
 *   the invited person      Accept → the console switches into the organization and opens the landing the API named.
 * Accepting creates no organization, workspace, subscription or trial — the notice says so, as the prototype does.
 *
 * Every unusable link (used, expired, revoked, superseded, never issued) is one page: the API gives no reason.
 *
 * AMENDED 2026-10-06 (plan-014 B3, hand-offs from INT):
 *   · the token is the link's FRAGMENT (`/invitation#<token>`), never its path, like every MAIL link — the router
 *     hands it over; a link with no token is the unusable-link page;
 *   · "Create a sign-in" happens HERE, for the invited address only (owner answer 45): the name and a password, sent
 *     with the invitation's token, so the new sign-in founds no organization, the address is verified by the emailed
 *     token and no second verification mail is sent; the page then offers Accept.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import type { AccountRole } from "@wetalk/domain";
import { Button, Field, Input } from "@wetalk/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { AuthSessionUnscopedError, type WorkspaceRoleView } from "../../../api/gateway.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import type { InvitationLandingView, InvitationLinkView } from "../../../api/slice/account-team.js";
import { landingPathOf } from "../../../scope/target.js";
import { AuthenticatorStep } from "./authenticator-step.js";
import { GLYPH } from "../../../shell/glyph.js";
import { ItemColumn, ItemRow, Notice } from "../../account-admin/page/organization-page.js";
import { ACCOUNT_ROLE_WORD, WORKSPACE_ROLE_WORD } from "../../account-admin/page/role-copy.js";

import "../../account-admin/page/organization-page.css";
import { PublicFrame, PublicKicker } from "../../auth/public-frame.js";

/** The page's search parameters. None: the token is the fragment. */
export interface InvitationAcceptSearch {}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page.
 */
export function invitationAcceptSearchOf(_raw: Readonly<Record<string, unknown>>): InvitationAcceptSearch {
  return {};
}

export interface InvitationAcceptScreenProps {
  /** The invitation token from the link's fragment, exactly as it arrived (never logged); empty for none. */
  readonly token: string;
  readonly search: InvitationAcceptSearch;
  readonly onSearch: (next: InvitationAcceptSearch) => void;
}

/** What each organization role gives, in the words of the accept page. */
const ACCOUNT_ROLE_DEK: Readonly<Record<AccountRole, string>> = {
  get owner() { return portalText("core", "start.runs_the_organization_its_people_and_its_money_024051"); },
  get admin() { return portalText("core", "start.runs_the_organization_s_workspaces_and_people_conversations_need_a6a19b"); },
  get finance() { return portalText("core", "start.balances_invoices_and_plans_7824c5"); },
  get viewer() { return portalText("core", "start.the_organization_s_profile_and_workspace_list_7bd3b4"); },
};

/** What each workspace role gives, in the words of the accept page. */
const WORKSPACE_ROLE_DEK: Readonly<Record<WorkspaceRoleView, string>> = {
  get owner() { return portalText("core", "start.everything_in_this_workspace_its_team_included_e6283e"); },
  get admin() { return portalText("core", "start.runs_this_workspace_and_its_team_c3ae2f"); },
  get member() { return portalText("core", "start.agents_campaigns_and_conversations_c8c8c0"); },
  get viewer() { return portalText("core", "start.reads_conversations_and_summaries_9e654e"); },
  get developer() { return portalText("core", "start.agents_documents_and_scoped_api_keys_12cf8c"); },
  get campaign_manager() { return portalText("core", "start.campaigns_and_contact_lists_8d1f1f"); },
  get billing() { return portalText("core", "start.this_workspace_s_spend_4dc43b"); },
};

function Page(props: { readonly title: string; readonly lede: string; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("core");
  return (
    // ADR 0056 (portal amendment): an invitation is a way in, so it is drawn in the public design's frame like Log in and Sign up.
    <PublicFrame width="wide">
      <section className="op-page">
        <PublicKicker>{portalText("core", "auth.entry.welcome")}</PublicKicker>
        <h1 className="op-title">{props.title}</h1>
        <p className="op-lede">{props.lede}</p>
        {props.children}
      </section>
    </PublicFrame>
  );
}

function OfferList(props: { readonly view: InvitationLinkView }): ReactNode {
  usePortalTranslation("core");
  const { view } = props;
  return (
    <ItemColumn label={portalText("core", "start.what_you_are_offered_304c27")}>
      {view.accountRole === null ? null : (
        <ItemRow
          glyph={GLYPH.buildings}
          tone="neutral"
          title={view.accountName}
          tag={ACCOUNT_ROLE_WORD[view.accountRole]}
          dek={`${ACCOUNT_ROLE_DEK[view.accountRole]}${view.workspaceRole.length === 0 ? portalText("core", "start._no_workspace_so_no_conversations_c8c77e") : ""}`}
          meta={portalText("core", "start.organization_7cb060")}
        />
      )}
      {view.workspaceRole.map((one) => (
        <ItemRow
          key={one.workspaceId}
          glyph={one.available ? GLYPH.buildings : GLYPH.archive}
          tone={one.available ? "accent" : "neutral"}
          title={one.workspaceName ?? portalText("core", "start.a_workspace_22656e")}
          tag={WORKSPACE_ROLE_WORD[one.memberRole]}
          dek={one.available ? WORKSPACE_ROLE_DEK[one.memberRole] : portalText("core", "start.archived_since_the_invitation_was_sent_it_will_not_be_included_cf0ef7")}
          meta={portalText("core", "start.workspace_a279fe")}
        />
      ))}
    </ItemColumn>
  );
}

/** Signs in with `signIn`; a sign-in in no or several organizations is open all the same (accepting comes next). */
async function signedIn(signIn: () => Promise<unknown>): Promise<void> {
  try {
    await signIn();
  } catch (raised) {
    if (!(raised instanceof AuthSessionUnscopedError)) throw raised;
  }
}

/** 2026-11-03: the code field the page shows once a code was mailed to the invited address. */
function EmailedCode(props: {
  readonly expiresAt: string;
  readonly code: string;
  readonly onCode: (code: string) => void;
  readonly problem: string | null;
}): ReactNode {
  usePortalTranslation("core");
  return (
    <>
      <p className="c-note">
        {portalText("core", "auth.join_code.on_its_way")}{" "}
        {new Date(props.expiresAt).toLocaleTimeString(portalLocale() === "en" ? "en-GB" : "el-GR", { hour: "2-digit", minute: "2-digit" })}.
      </p>
      <Field label={portalText("core", "auth.sign_in_code_6af151")} {...(props.problem === null ? {} : { error: props.problem })}>
        {(control) => (
          <Input {...control} inputMode="numeric" autoComplete="one-time-code" value={props.code} onChange={(event) => props.onCode(event.currentTarget.value)} />
        )}
      </Field>
    </>
  );
}

function LogInHere(props: { readonly email: string; readonly token: string; readonly codeAvailable: boolean; readonly onSignedIn: () => void }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [codeSent, setCodeSent] = useState<{ readonly expiresAt: string } | null>(null);
  const [code, setCode] = useState("");
  const askForCode = useMutation({
    mutationFn: () => gateway.requestInvitationSignInCode(props.token),
    onSuccess: (asked) => {
      setProblem(null);
      setCode("");
      setCodeSent(asked);
    },
    onError: (raised: Error) => setProblem(() => (apiProblemText(raised, portalText("core", "start.you_could_not_be_signed_in_da9633")))),
  });
  const withCode = useMutation({
    mutationFn: () => signedIn(() => gateway.verifySignInCode(code.replace(/\s/g, ""))),
    onSuccess: async () => {
      setCode("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
      props.onSignedIn();
    },
    onError: (raised: Error) => setProblem(() => (apiProblemText(raised, portalText("core", "start.you_could_not_be_signed_in_da9633")))),
  });
  const logIn = useMutation({
    mutationFn: () => signedIn(() => gateway.logIn({ emailAddress: props.email, password })),
    onSuccess: async () => {
      setPassword("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
      props.onSignedIn();
    },
    onError: (raised: Error) => {
      setPassword("");
      setProblem(() => (raised instanceof ApiError && raised.code === "credential_invalid" ? portalText("core", "start.that_password_is_not_right_930b19") : apiProblemText(raised, portalText("core", "start.you_could_not_be_signed_in_da9633"))));
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (password !== "") logIn.mutate();
  };
  if (codeSent !== null) {
    return (
      <form
        className="op-form"
        aria-label={portalText("core", "start.log_in_to_accept_6e1aa9")}
        onSubmit={(event) => {
          event.preventDefault();
          setProblem(null);
          if (code.trim() === "") {
            setProblem(() => (portalText("core", "auth.enter_the_6_digit_code_from_the_email_029180")));
            return;
          }
          withCode.mutate();
        }}
      >
        <Field label={portalText("core", "auth.email_84add5")}>{(control) => <Input {...control} value={props.email} readOnly />}</Field>
        <EmailedCode expiresAt={codeSent.expiresAt} code={code} onCode={setCode} problem={problem} />
        <span className="op-form-actions">
          <Button variant="primary" type="submit" disabled={withCode.isPending}>{portalText("core", "auth.log_in_f7c400")}</Button>
          <Button variant="secondary" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>{portalText("core", "auth.send_a_new_code_91904a")}</Button>
          <Button
            variant="ghost"
            onClick={() => {
              setCodeSent(null);
              setProblem(null);
            }}
          >
            {portalText("core", "auth.use_my_password_instead_b5501c")}
          </Button>
        </span>
      </form>
    );
  }
  return (
    <form className="op-form" onSubmit={submit} aria-label={portalText("core", "start.log_in_to_accept_6e1aa9")}>
      <Field label={portalText("core", "auth.email_84add5")}>{(control) => <Input {...control} value={props.email} readOnly />}</Field>
      <Field label={portalText("core", "auth.password_8be3c9")} {...(problem === null ? {} : { error: problem })}>
        {(control) => <Input {...control} type="password" value={password} autoComplete="current-password" onChange={(event) => setPassword(event.target.value)} />}
      </Field>
      <span className="op-form-actions">
        <Button variant="primary" type="submit" disabled={logIn.isPending || password === ""}>{portalText("core", "auth.log_in_f7c400")}</Button>
        {props.codeAvailable ? (
          <Button variant="ghost" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
            {portalText("core", "auth.email_me_a_sign_in_code_instead_d078c2")}
          </Button>
        ) : null}
      </span>
    </form>
  );
}

/**
 * "Create your WeTalk sign-in" for the invited address (owner answer 45): the person only — no organization — and the
 * address verified by the invitation itself, so no verification mail follows. On success the page reads the link again
 * and offers Accept.
 */
function SignUpHere(props: { readonly email: string; readonly token: string; readonly codeAvailable: boolean; readonly onSignedUp: () => void }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [problem, setProblem] = usePortalMessage();
  // 2026-11-03: the emailed code instead of a password.
  const [codeSent, setCodeSent] = useState<{ readonly expiresAt: string } | null>(null);
  const [code, setCode] = useState("");
  const askForCode = useMutation({
    mutationFn: () => gateway.requestInvitationSignInCode(props.token),
    onSuccess: (asked) => {
      setProblem(null);
      setCode("");
      setCodeSent(asked);
    },
    onError: (raised: Error) => setProblem(() => (apiProblemText(raised, portalText("core", "start.your_sign_in_could_not_be_created_eb243d")))),
  });
  const signUp = useMutation({
    mutationFn: () =>
      gateway.signUpFromInvitation(
        props.token,
        codeSent === null
          ? { email: props.email, displayName: displayName.trim(), password, code: null }
          : { email: props.email, displayName: displayName.trim(), password: null, code: code.replace(/\s/g, "") },
      ),
    onSuccess: async () => {
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
      props.onSignedUp();
    },
    onError: (raised: Error) => {
      setProblem(
        () => (raised instanceof ApiError && raised.code === "email_taken"
          ? portalText("core", "start._already_has_a_wetalk_sign_in_log_in_with_it_instead_2da44c", { value0: props.email })
          : apiProblemText(raised, portalText("core", "start.your_sign_in_could_not_be_created_eb243d"))),
      );
    },
  });
  return (
    <form
      className="op-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        signUp.mutate();
      }}
    >
      <Field label={portalText("core", "auth.email_84add5")} description={portalText("core", "start.the_invited_address_accepting_verifies_it_910e3e")}>
        {(control) => <Input {...control} type="email" value={props.email} readOnly />}
      </Field>
      <Field label={portalText("core", "auth.your_name_ab4229")} description={portalText("core", "start.as_the_people_in_the_organization_will_see_it_cc419c")}>
        {(control) => (
          <Input
            {...control}
            autoComplete="name"
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      {codeSent === null ? (
        <Field label={portalText("core", "start.choose_a_password_5ce218")} description={portalText("core", "auth.at_least_10_characters_37e136")}>
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
              }}
            />
          )}
        </Field>
      ) : (
        <>
          <EmailedCode expiresAt={codeSent.expiresAt} code={code} onCode={setCode} problem={null} />
          <p className="c-note">{portalText("core", "auth.join_code.password_later")}</p>
        </>
      )}
      {problem === null ? null : (
        <p className="op-toast op-toast-problem" role="status">
          <span>{problem}</span>
        </p>
      )}
      <span className="op-form-actions">
        <Button
          variant="primary"
          type="submit"
          disabled={signUp.isPending || displayName.trim() === "" || (codeSent === null ? password === "" : code.trim() === "")}
        >
          {signUp.isPending ? portalText("core", "start.creating_94d7d8") : portalText("core", "start.create_my_sign_in_20a4fd")}
        </Button>
        {codeSent !== null ? (
          <>
            <Button variant="secondary" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>{portalText("core", "auth.send_a_new_code_91904a")}</Button>
            <Button
              variant="ghost"
              onClick={() => {
                setCodeSent(null);
                setProblem(null);
              }}
            >
              {portalText("core", "auth.join_code.set_password_instead")}
            </Button>
          </>
        ) : props.codeAvailable ? (
          <Button variant="ghost" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
            {portalText("core", "start.code.create_with_code_instead")}
          </Button>
        ) : null}
      </span>
    </form>
  );
}

export function InvitationAcceptScreen(props: InvitationAcceptScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [problem, setProblem] = usePortalMessage();
  // plan-014 review I-18: the organization's policy asks for an authenticator first — met on this page.
  const [authenticatorAsked, setAuthenticatorAsked] = useState(false);
  // 2026-11-03: whether an emailed code can be sent at all (as Log in asks). A failed read offers nothing.
  const codeAvailability = useQuery({
    queryKey: ["personal-security", "sign-in-code-availability"],
    queryFn: () => gateway.readSignInCodeAvailability(),
    enabled: props.token !== "",
    staleTime: 300_000,
    retry: false,
  });
  const codeAvailable = codeAvailability.data === true;
  const link = useQuery({
    queryKey: ["account-team", "invitation-link", props.token],
    queryFn: () => gateway.inspectAccountInvitation(props.token),
    retry: false,
    staleTime: 0,
    // A link with no token names no invitation: the unusable-link page, without asking the API.
    enabled: props.token !== "",
  });

  const enter = async (accountId: string, landing: InvitationLandingView): Promise<void> => {
    const switched = await gateway.switchScope({ accountId, workspaceId: landing.kind === "workspace" ? landing.workspaceId : null });
    queryClient.setQueryData(QUERY_KEY.authSession, switched.authSession);
    await navigate({ to: landingPathOf(switched.authSession) ?? "/" });
  };
  const accept = useMutation({
    mutationFn: async () => {
      const accepted = await gateway.acceptAccountInvitation(props.token);
      await enter(accepted.accountId, accepted.landing);
    },
    onError: async (raised: Error) => {
      await link.refetch();
      if (raised instanceof ApiError && raised.code === "assurance_required" && raised.field !== "single_sign_on") {
        setProblem(null);
        setAuthenticatorAsked(true);
        return;
      }
      setProblem(() => (apiProblemText(raised, portalText("core", "auth.the_invitation_could_not_be_accepted_50fd96"))));
    },
  });
  const signOut = useMutation({
    mutationFn: () => gateway.logOut(),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
      await link.refetch();
    },
  });

  if (props.token === "") {
    return (
      <Page
        title={portalText("core", "start.this_invitation_cannot_be_used_756bbf")}
        lede={portalText("core", "start.the_link_is_incomplete_open_it_again_from_the_email_or_ask_whoeve_27c6f4")}
      >
        <div className="op-block" />
      </Page>
    );
  }
  if (link.isPending) {
    return (
      <Page title={portalText("core", "start.opening_your_invitation_7c9048")} lede={portalText("core", "start.one_moment_b0fb11")}>
        <div className="op-block" />
      </Page>
    );
  }
  if (link.isError) {
    const unusable = link.error instanceof ApiError && link.error.code === "invitation_unavailable";
    return (
      <Page
        title={unusable ? portalText("core", "start.this_invitation_cannot_be_used_756bbf") : portalText("core", "start.the_invitation_could_not_be_opened_4fbcf7")}
        lede={unusable ? portalText("core", "start.it_was_already_used_it_expired_or_it_was_withdrawn_ask_whoever_in_0d407d") : apiProblemText(link.error, portalText("core", "start.try_the_link_again_in_a_moment_87d514"))}
      >
        <div className="op-block">
          <Notice
            tone="neutral"
            glyph={GLYPH.envelope}
            title={portalText("core", "start.already_a_member_052100")}
            text={portalText("core", "start.log_in_to_open_the_organizations_you_belong_to_73942d")}
            actions={
              <Link to="/log-in" className="btn btn-secondary">{portalText("core", "auth.log_in_f7c400")}</Link>
            }
          />
        </div>
      </Page>
    );
  }
  const view = link.data;
  const lede = portalText("core", "start.you_were_invited_as_accepting_gives_you_exactly_the_roles_below_a_319acf", { value0: view.email });
  const policyTitle = view.requiresAuthenticator ? portalText("core", "start.this_organization_requires_an_authenticator_790f2c") : portalText("core", "start.nothing_else_is_created_45e88e");
  const policyText = view.requiresAuthenticator
    ? portalText("core", "start.you_will_be_asked_to_add_one_before_you_can_go_in_no_new_organiza_592f87")
    : portalText("core", "start.no_new_organization_workspace_or_trial_is_created_by_accepting_yo_c08fb3");

  let notice: ReactNode;
  if (view.state === "accepted" && view.landing !== null) {
    const landing = view.landing;
    notice = (
      <Notice
        tone="accent"
        glyph={GLYPH.checkCircle}
        title={portalText("core", "start.you_already_accepted_this_invitation_0976d3")}
        text={portalText("core", "start.you_are_a_member_of_bba940", { value0: view.accountName })}
        actions={
          <Button variant="primary" onClick={() => void enter(view.accountId, landing).catch((raised: unknown) => setProblem(() => (apiProblemText(raised, portalText("core", "start.the_organization_could_not_be_opened_2eca9d")))))}>{portalText("core", "start.go_there_cf4d8a")}</Button>
        }
      />
    );
  } else if (view.viewer === null) {
    notice = view.signInExists ? (
      <>
        <Notice tone="neutral" glyph={GLYPH.lockSimple} title={portalText("core", "start.log_in_as_to_accept_3be0ee", { value0: view.email })} text={portalText("core", "start.only_the_invited_address_can_accept_this_invitation_4a6204")} />
        <div className="op-block">
          <LogInHere email={view.email} token={props.token} codeAvailable={codeAvailable} onSignedIn={() => void link.refetch()} />
        </div>
      </>
    ) : (
      <>
        <Notice
          tone="neutral"
          glyph={GLYPH.personPlus}
          title={portalText("core", "start.create_your_wetalk_sign_in_with_eca92a", { value0: view.email })}
          text={portalText("core", "start.then_accept_below_the_invitation_verifies_the_address_and_you_wil_3681f7")}
        />
        <div className="op-block">
          <SignUpHere email={view.email} token={props.token} codeAvailable={codeAvailable} onSignedUp={() => void link.refetch()} />
        </div>
      </>
    );
  } else if (!view.viewer.matches) {
    notice = (
      <Notice
        tone="neutral"
        glyph={GLYPH.personSwitch}
        title={portalText("core", "start.this_invitation_is_for_96eff3", { value0: view.email })}
        text={portalText("core", "start.you_are_signed_in_as_sign_out_then_log_in_with_the_invited_addres_717efa", { value0: view.viewer.email })}
        actions={
          <Button variant="secondary" onClick={() => signOut.mutate()} disabled={signOut.isPending}>{portalText("core", "start.sign_out_dc1649")}</Button>
        }
      />
    );
  } else {
    // Owner answer 45: an invitee who has not verified the invited address yet accepts all the same — the emailed
    // link is the proof, and accepting verifies the address.
    const verifies = view.viewer.emailVerified ? "" : portalText("core", "start._accepting_also_verifies_c859af", { value0: view.email });
    notice = authenticatorAsked ? (
      <AuthenticatorStep
        accountName={view.accountName}
        email={view.email}
        onDone={() => {
          setAuthenticatorAsked(false);
          accept.mutate();
        }}
      />
    ) : (
      <Notice
        tone="neutral"
        glyph={GLYPH.shieldCheck}
        title={policyTitle}
        text={`${policyText}${verifies}`}
        actions={
          <Button variant="secondary" onClick={() => accept.mutate()} disabled={accept.isPending}>
            {accept.isPending ? portalText("core", "start.accepting_50e0f2") : portalText("core", "account_admin.accept_bb54db")}
          </Button>
        }
      />
    );
  }

  return (
    <Page title={portalText("core", "auth.join_3ebf99", { value0: view.accountName })} lede={lede}>
      {problem === null ? null : (
        <p className="op-toast op-toast-problem" role="status">
          <span>{problem}</span>
        </p>
      )}
      <div className="op-block">
        <OfferList view={view} />
      </div>
      <div className="op-block">{notice}</div>
    </Page>
  );
}
